import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUsersCog,
  FaTasks,
  FaClipboardList,
  FaUserPlus,
  FaChartPie,
  FaShieldAlt,
  FaSyncAlt,
  FaFolderOpen,
} from "react-icons/fa";
import DashboardLayout from "../layouts/DashboardLayout";
import "./AdminDashboard.css";

const AdminDashboard = () => {
  const navigate = useNavigate();

  const headlineMetrics = useMemo(
    () => [
      {
        label: "Active Users",
        value: "128",
        deltaLabel: "+12 new",
        icon: <FaUsersCog />,
        accent: "#4caf50",
      },
      {
        label: "Open Tasks",
        value: "342",
        deltaLabel: "58 high priority",
        icon: <FaTasks />,
        accent: "#ff9800",
      },
      {
        label: "Pending Approvals",
        value: "19",
        deltaLabel: "3 critical",
        icon: <FaClipboardList />,
        accent: "#e53935",
      },
      {
        label: "Integrations",
        value: "Google",
        deltaLabel: "Last sync 14m ago",
        icon: <FaSyncAlt />,
        accent: "#1a73e8",
      },
    ],
    []
  );

  const quickActions = useMemo(
    () => [
      {
        label: "Invite user",
        description: "Create a new account and assign default roles.",
        icon: <FaUserPlus />,
        onClick: () => navigate("/create-user"),
      },
      {
        label: "Manage roles",
        description: "Review permissions and adjust access policies.",
        icon: <FaShieldAlt />,
        onClick: () => navigate("/roles"),
      },
      {
        label: "Project catalog",
        description: "Track ownership and department coverage.",
        icon: <FaFolderOpen />,
        onClick: () => navigate("/project"),
      },
      {
        label: "Task insights",
        description: "Drill into escalations and overdue work.",
        icon: <FaChartPie />,
        onClick: () => navigate("/task"),
      },
    ],
    [navigate]
  );

  const reviewQueue = useMemo(
    () => [
      {
        id: "USR-104",
        name: "Neha Sharma",
        request: "Role change: Analytics → Admin",
        submittedAt: "Nov 22, 10:18 AM",
        status: "Awaiting review",
      },
      {
        id: "TASK-871",
        name: "Monthly Reporting",
        request: "High priority escalation",
        submittedAt: "Nov 23, 4:52 PM",
        status: "Needs triage",
      },
      {
        id: "GRP-214",
        name: "All Hands",
        request: "Add 4 members",
        submittedAt: "Nov 21, 6:40 PM",
        status: "Queued",
      },
    ],
    []
  );

  const activityFeed = useMemo(
    () => [
      {
        id: 1,
        actor: "Shubham Singh",
        action: "updated permissions for Delegation Managers",
        when: "5 minutes ago",
      },
      {
        id: 2,
        actor: "Aarti Patel",
        action: "synced 38 tasks to Google Workspace",
        when: "16 minutes ago",
      },
      {
        id: 3,
        actor: "Rakesh Mehta",
        action: "created project \"Retail Transformation\"",
        when: "Yesterday, 9:12 PM",
      },
    ],
    []
  );

  return (
    <DashboardLayout>
      <div className="admin-page">
        <header className="admin-header">
          <div>
            <h1>Administration Center</h1>
            <p>
              Monitor critical signals, approve changes, and keep workspaces in
              sync.
            </p>
          </div>
          <button
            type="button"
            className="admin-primary-btn"
            onClick={() => navigate("/create-user")}
          >
            Add administrator
          </button>
        </header>

        <section className="admin-metrics">
          {headlineMetrics.map((metric) => (
            <article
              key={metric.label}
              className="admin-metric-card"
              style={{ "--metric-accent": metric.accent }}
            >
              <div className="admin-metric-icon">{metric.icon}</div>
              <div className="admin-metric-body">
                <span className="admin-metric-label">{metric.label}</span>
                <strong className="admin-metric-value">{metric.value}</strong>
                <span className="admin-metric-delta">{metric.deltaLabel}</span>
              </div>
            </article>
          ))}
        </section>

        <section className="admin-grid">
          <aside className="admin-panel admin-panel--actions">
            <header>
              <h2>Quick actions</h2>
              <p>Jump straight into the workflows you moderate most.</p>
            </header>
            <div className="admin-action-list">
              {quickActions.map((action) => (
                <button
                  key={action.label}
                  type="button"
                  className="admin-action-item"
                  onClick={action.onClick}
                >
                  <span className="admin-action-icon">{action.icon}</span>
                  <span className="admin-action-copy">
                    <strong>{action.label}</strong>
                    <small>{action.description}</small>
                  </span>
                </button>
              ))}
            </div>
          </aside>

          <section className="admin-panel admin-panel--queue">
            <header>
              <h2>Approval queue</h2>
              <p>Resolve elevated requests and keep governance timely.</p>
            </header>
            <div className="admin-table">
              <div className="admin-table-head">
                <span>ID</span>
                <span>Item</span>
                <span>Request</span>
                <span>Submitted</span>
                <span>Status</span>
              </div>
              <div className="admin-table-body">
                {reviewQueue.map((entry) => (
                  <div key={entry.id} className="admin-table-row">
                    <span>{entry.id}</span>
                    <span>{entry.name}</span>
                    <span>{entry.request}</span>
                    <span>{entry.submittedAt}</span>
                    <span>
                      <span className="admin-status-pill">{entry.status}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </section>

        <section className="admin-panel admin-panel--activity">
          <header>
            <h2>Latest activity</h2>
            <p>Track notable changes performed by workspace administrators.</p>
          </header>
          <ul className="admin-activity-feed">
            {activityFeed.map((event) => (
              <li key={event.id}>
                <span className="admin-activity-dot" />
                <div>
                  <strong>{event.actor}</strong> {event.action}
                  <time>{event.when}</time>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
